import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Divider,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import TrendingDownRounded from '@mui/icons-material/TrendingDownRounded';
import PaidRounded from '@mui/icons-material/PaidRounded';
import ListAltRounded from '@mui/icons-material/ListAltRounded';
import PeopleAltRounded from '@mui/icons-material/PeopleAltRounded';
import CompareArrowsRounded from '@mui/icons-material/CompareArrowsRounded';
import { useFeature } from '../../ui/FeaturesProvider';
import {
  downloadPlanRosterOptimizedXlsx,
  fetchAgentSchedule,
  fetchPlanCapacity,
  fetchPlanRosterOptimized,
  type FetchOpts,
} from '../../services/contactOptimizationService';
import { SUPPORTED_SHIFT_WORK_HOURS } from '../../types/contactOptimization';
import type {
  AgentScheduleResponse,
  AgentShift,
  OptimizedRoster,
  PlanCapacity,
  HourStatus,
  PlanRosterOptimizedResponse,
  RosterHour,
  RosterShiftSegment,
  StatePriorityEntry,
} from '../../types/contactOptimization';
import SettingsPageHeader from '../settings/components/SettingsPageHeader';
import { formatDateRange } from '../../utils/date';
import { SectionScopeChip } from './ScopeBanner';
import ContactOptimizationNav from './ContactOptimizationNav';
import { RefreshProvider, useRefreshRegistration, useReportFreshness } from './RefreshController';
import ExplainerNote, { ExplainerToggle } from './ExplainerNote';
import ChartSectionHeader from './ChartSectionHeader';
import RosterGrid, { type RosterGridCoverageHour, type RosterGridRow } from './RosterGrid';
import { ExpectedContactRateTiles, useExpectedContactRate } from './ExpectedContactRate';
import { MetricTile } from './MetricTile';
import { CapacityChart, DEMAND_TIER_LABELS } from './planCharts';

function pct(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined) return '\u2013';
  return `${(value * 100).toFixed(digits)}%`;
}

function fmtMoney(value: number | null | undefined): string {
  if (value === null || value === undefined) return '\u2013';
  return `$${Math.round(value).toLocaleString()}`;
}

/** Total agent-hours the CURRENT roster provides in a week -- each
 * agent's own shift span (real or inferred, whichever this agent has)
 * times the number of weekday operating days, summed across everyone
 * with a usable shift. The same "usable shift" filter the roster
 * optimizer itself uses (agents with no real/inferred shift can't be
 * counted, there's nothing to sum). */
/** Hours a week the team is SCHEDULED to spend on the phone, taken from
 * the roster the optimizer actually produces (every agent on the standard
 * shift) rather than from the shifts they work today.
 *
 * Two reasons this is the roster's number and not today's. It is what the
 * team is being scheduled for going forward, which is the question the
 * tile asks. And today's shifts are start-to-end SPANS that include each
 * agent's break, so summing them counts break time as calling capacity --
 * the roster's work_hours does not.
 *
 * Falls back to today's spans while the roster is still loading, so the
 * tile shows a real number rather than a dash. */
function scheduledWeeklyAgentHours(
  roster: PlanRosterOptimizedResponse | null,
  schedule: AgentScheduleResponse | null,
  weekdayOperatingDays: number,
): { hours: number | null; fromRoster: boolean } {
  const assignments = roster?.optimized.assignments ?? [];
  if (assignments.length) {
    const daily = assignments.reduce((sum, a) => sum + (a.work_hours ?? a.span_hours ?? 0), 0);
    if (daily > 0) return { hours: daily * weekdayOperatingDays, fromRoster: true };
  }
  if (!schedule) return { hours: null, fromRoster: false };
  const daily = schedule.agents.reduce((sum, a) => {
    if (a.shift_start_hour == null || a.shift_end_hour == null) return sum;
    return sum + (a.shift_end_hour - a.shift_start_hour);
  }, 0);
  return { hours: daily * weekdayOperatingDays, fromRoster: false };
}

function exportPlanCsv(plan: PlanCapacity) {
  const header = [
    'hour',
    'total_calls',
    'connect_rate',
    'staffed_agents',
    'required_agents',
    'agents_short',
    'agents_extra',
    'demand_tier',
  ];
  const lines = [header.join(',')];
  for (const h of plan.hours) {
    lines.push(
      [
        h.hour_label,
        h.total_calls,
        h.connect_rate?.toFixed(4) ?? '',
        h.staffed_agents,
        h.required_agents ?? '',
        h.shortfall_agents ?? '',
        h.surplus_agents ?? '',
        h.demand_tier ?? '',
      ].join(','),
    );
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'plan-capacity.csv';
  a.click();
  URL.revokeObjectURL(url);
}

function fmtHourDecimal(h: number): string {
  const hh = Math.floor(((h % 24) + 24) % 24);
  const mins = Math.round((h - Math.floor(h)) * 60);
  const period = hh >= 12 ? 'pm' : 'am';
  const hour12 = hh % 12 === 0 ? 12 : hh % 12;
  return mins === 0 ? `${hour12}${period}` : `${hour12}:${mins.toString().padStart(2, '0')}${period}`;
}

/** "6h earlier" / "1h 30m later" -- the direction + magnitude of a
 * roster move, phrased the way a human would say it out loud rather than
 * as a raw "start1 -> start2" pair (still shown alongside this, not
 * instead of it). */
function fmtMovedDelta(movedHours: number): { arrow: string; text: string } {
  const abs = Math.abs(movedHours);
  const hh = Math.floor(abs + 1e-6);
  const mm = Math.round((abs - hh) * 60);
  const parts: string[] = [];
  if (hh > 0) parts.push(`${hh}h`);
  if (mm > 0) parts.push(`${mm}m`);
  const magnitude = parts.length ? parts.join(' ') : '0m';
  return movedHours < 0 ? { arrow: '\u25bc', text: `${magnitude} earlier` } : { arrow: '\u25b2', text: `${magnitude} later` };
}

/** Today's real shift as timeline segments. We know each agent's start
 * and end from the imported schedule, but NOT where their break falls --
 * the source spreadsheet carries a break length in free text and no
 * placement -- so a real agent draws as one continuous bar rather than
 * inventing a break position. Inferred agents do have an estimated break
 * hour, and that one is drawn. */
function asGivenToSegments(a: AgentShift): RosterShiftSegment[] {
  const start = a.shift_start_hour;
  const end = a.shift_end_hour;
  if (start == null || end == null || end <= start) return [];
  if (a.break_hour != null && a.break_hour > start && a.break_hour + 1 <= end) {
    return [
      { kind: 'work', start, end: a.break_hour },
      { kind: 'break', start: a.break_hour, end: a.break_hour + 1 },
      { kind: 'work', start: a.break_hour + 1, end },
    ];
  }
  return [{ kind: 'work', start, end }];
}

/** Fallback for rosters with no `segments` (continuous solver): collapse
 * the 24-length hour array into runs. Whole-hour precision only -- that
 * is all the array itself has. */
function hourStatusesToSegments(hours: HourStatus[]): RosterShiftSegment[] {
  const segments: RosterShiftSegment[] = [];
  let run: { kind: 'work' | 'break'; start: number; end: number } | null = null;
  hours.forEach((status, hour) => {
    if (status === 'off') {
      if (run) segments.push(run);
      run = null;
      return;
    }
    const kind = status === 'break' ? 'break' : 'work';
    if (run && run.kind === kind && run.end === hour) {
      run.end = hour + 1;
    } else {
      if (run) segments.push(run);
      run = { kind, start: hour, end: hour + 1 };
    }
  });
  if (run) segments.push(run);
  return segments;
}

/** "split into two blocks of at most 4.5 hours each, separated by one
 * break of at least an hour" -- the shift's shape, in the words the
 * explainer uses, for whichever roster view is on screen.
 *
 * Derived from the same rule the solver applies (a break every 4.5 hours,
 * each at least 30 minutes, together adding up to at least an hour) so the
 * explanation cannot drift from the roster it explains.
 */
function rosterShapeSentence(hours: number): string {
  const blocks = Math.ceil(hours / 4.5 - 1e-9);
  const breaks = blocks - 1;
  const words = ['zero', 'one', 'two', 'three', 'four'];
  if (breaks < 1) return `worked as a single block of at most 4.5 hours`;
  const breakLength = breaks === 1 ? 'at least an hour' : 'at least 30 minutes';
  const rationale =
    breaks === 1 ? 'a rest break and a lunch combined' : 'a rest break and a lunch, scheduled separately';
  return (
    `split into ${words[blocks]} work blocks of at most 4.5 hours each, separated by ` +
    `${words[breaks]} ${breaks === 1 ? 'break' : 'breaks'} of ${breakLength} (${rationale})`
  );
}

/** The Roster section's views.
 *
 * Started life as a shift-length toggle (8/10/12) plus a separate
 * "Compare with current schedule" switch. Those were two controls over one
 * question -- which roster am I looking at -- and the compare switch could
 * not answer it alone, since it overlaid today's schedule on the solved
 * one rather than showing it. One dropdown, one answer.
 *
 * Deliberately open-ended: AM/PM split shifts are a real the customer ask and
 * are listed here disabled rather than left out, so the control's shape
 * does not have to change when they land.
 */
type RosterViewId = 'current' | 'h8' | 'h10' | 'h12' | 'ampm';

const ROSTER_VIEWS: {
  id: RosterViewId;
  label: string;
  hours: number | null;
  disabled?: boolean;
  note?: string;
}[] = [
  { id: 'current', label: "Today's schedule", hours: null },
  ...SUPPORTED_SHIFT_WORK_HOURS.map((h) => ({
    id: `h${h}` as RosterViewId,
    label: `${h}-hour shift`,
    hours: h,
  })),
  { id: 'ampm', label: 'AM/PM split shift', hours: null, disabled: true, note: 'Coming soon' },
];

// Five options, one of which is the baseline and four of which are
// proposals against it. That distinction had "As scheduled"/"Optimized"
// group headings, which sat at the same size and left edge as the rows
// and so read as rows themselves -- options you could click but that did
// nothing. A divider carries the same split with nothing clickable about
// it, which is the whole of what the headings were for at this list
// length.
const ROSTER_ITEM_SX = { px: 1.75, fontSize: 13 };

function rosterViewById(id: RosterViewId) {
  return ROSTER_VIEWS.find((v) => v.id === id) ?? ROSTER_VIEWS[1];
}

function OptimizedRosterView({
  optimized,
  asGivenAgents,
  referenceTimezone,
  hours,
  statePriority,
  view,
  updating,
  solvedShiftHours,
}: {
  optimized: OptimizedRoster;
  asGivenAgents: AgentShift[];
  referenceTimezone: string;
  hours: RosterHour[];
  statePriority?: Record<string, StatePriorityEntry[]>;
  /** Which roster to draw. ``current`` shows the schedule agents actually
   * work today; the rest show the solved roster on the payload. */
  view: RosterViewId;
  /** A solved view is being fetched. The grid keeps its previous contents
   * under a shimmer rather than emptying, so the page does not jump. */
  updating: boolean;
  /** The shift length the payload says it was solved for. */
  solvedShiftHours: number | null;
}) {
  const showingCurrent = view === 'current';
  // What the GRID is, preferring what the backend says it solved over what
  // the dropdown asked for. The two agree in the normal case; when they do
  // not -- a payload cached before the roster reported its own hours, say
  // -- the grid is the authority, because the grid is what the reader is
  // looking at. Falling back to the requested view is safe either way:
  // both are the roster on screen, unlike the old fallback to
  // ``scenarios[0]``, which was a schedule nobody had asked to see.
  const shiftWorkHours = solvedShiftHours ?? rosterViewById(view).hours;
  // Today's schedule as its own set of rows -- the same grid, drawn from
  // the as-given shifts rather than the solver's assignments. It carries
  // no "moved" badge because nothing has been moved: this IS the baseline
  // every other view is a proposal against.
  const currentRows: RosterGridRow[] = asGivenAgents.map((a) => ({
    agent_full_nm: a.agent_full_nm,
    source: a.source,
    sufficient_history: a.sufficient_history,
    segments: asGivenToSegments(a),
    homeTz: a.home_tz,
    workHours:
      a.shift_start_hour != null && a.shift_end_hour != null ? a.shift_end_hour - a.shift_start_hour : null,
    breakHours: a.break_hour != null ? 1 : null,
    badge: 'today',
    tooltipExtra: 'What this agent is scheduled for today. Every other roster view is measured against it.',
  }));
  const optimizedRows: RosterGridRow[] = optimized.assignments.map((a) => {
    const moved = Math.abs(a.moved_hours) >= 0.5;
    const delta = fmtMovedDelta(a.moved_hours);
    return {
      agent_full_nm: a.agent_full_nm,
      source: a.source,
      sufficient_history: true,
      // `segments` is the split solver's exact geometry; the fallback
      // covers the continuous solver, which doesn't emit them.
      segments: a.segments ?? hourStatusesToSegments(a.hours),
      // Today's schedule is a view of its own now, so it is no longer
      // overlaid as a second bar on the optimized one.
      compareSegments: undefined,
      homeTz: a.home_tz,
      workHours: a.work_hours ?? a.span_hours,
      breakHours: a.break_hours ?? null,
      badge: moved ? `${delta.arrow} ${delta.text}` : 'unchanged',
      tooltipExtra: moved
        ? `Moved ${delta.text} \u2014 they start ${fmtHourDecimal(a.current_start_hour)} today.`
        : 'Already at their best start time, so this one is unchanged.',
    };
  });
  const rows = showingCurrent ? currentRows : optimizedRows;
  const coverage: RosterGridCoverageHour[] = hours.map((h) => {
    // "On shift" has to describe the grid being drawn, or the header
    // counts a roster the reader cannot see.
    const onShift = showingCurrent
      ? optimized.coverage_by_hour_before?.[h.hour] ?? 0
      : optimized.coverage_by_hour_after?.[h.hour] ?? 0;
    const onShiftBefore = showingCurrent ? null : optimized.coverage_by_hour_before?.[h.hour] ?? null;
    const required = optimized.target_by_hour?.[h.hour] ?? null;
    return {
      hour: h.hour,
      agents_on_shift: onShift,
      agents_on_shift_before: onShiftBefore,
      required_agents: required,
      gap: required != null ? required - onShift : null,
    };
  });
  // Fetched here rather than at page level: it belongs to the roster
  // section, and capacity is what it measures.
  const { data: contactRate, loading: contactRateLoading } = useExpectedContactRate();
  // shortfall_agent_hours_* and expected_connects_per_day_* are still on
  // the payload and still shown in the roster grid's own comparison. Their
  // headline tiles are gone -- see the note above the tile row.


  return (
    <Box>
      {/* "Agent-hours short" and "Expected connects/day" used to sit here.
          Removed 2026-08-25: both describe the reshuffle rather than the
          outcome, and both are per-DIAL or per-hour where the question the
          business asks is per-LEAD. What replaced them is contact rate --
          what it is today, what this roster should make it, and the
          ceiling if headcount were no object. */}
      <ExpectedContactRateTiles
        data={contactRate}
        loading={contactRateLoading}
        view={showingCurrent ? { kind: 'current' } : { kind: 'shift', hours: shiftWorkHours ?? 8 }}
        updating={updating}
      />
      {/* A cold solve is 50-80 seconds. The grid stays in place and dims
          rather than blanking -- keeping the reader's scroll position and
          the shape of what they were looking at -- with the status
          anchored at the TOP of the region, where it reads as a banner
          over the section rather than as a dialog interrupting it.

          Deliberately no animated sweep across the grid: a shimmer sized
          to thirteen rows reads as a failure state. The spinner in the
          banner already carries "working". */}
      <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            transition: 'opacity 150ms ease',
            opacity: updating ? 0.35 : 1,
            pointerEvents: updating ? 'none' : 'auto',
          }}
        >
          <RosterGrid
            rows={rows}
            coverage={coverage}
            hours={hours}
            referenceTimezone={referenceTimezone}
            statePriority={statePriority}
          />
        </Box>
        {updating && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'center',
              pt: 1,
              zIndex: 2,
              pointerEvents: 'none',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ px: 2, py: 0.75, borderRadius: 2, bgcolor: 'background.paper', boxShadow: 2 }}
            >
              <CircularProgress size={15} />
              <Typography variant="body2" color="text.secondary">
                Rebuilding the roster…
              </Typography>
            </Stack>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default function PlanPage() {
  return (
    <RefreshProvider>
      <PlanPageContent />
    </RefreshProvider>
  );
}

function PlanPageContent() {
  const feature = useFeature('contact_optimization');
  const [plan, setPlan] = React.useState<PlanCapacity | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [schedule, setSchedule] = React.useState<AgentScheduleResponse | null>(null);
  const [scheduleLoading, setScheduleLoading] = React.useState(true);
  const [scheduleError, setScheduleError] = React.useState<string | null>(null);
  const [showDemandHelp, setShowDemandHelp] = React.useState(false);
  const [showRosterHelp, setShowRosterHelp] = React.useState(false);

  // Recommended-hours coverage is no longer fetched: its only reader was
  // the "Best-hours coverage" tile, removed 2026-08-13 (it reported 100%
  // beside a large shortfall, asking only whether AT LEAST ONE agent was
  // on shift). Timing still owns that view.

  // The Roster section shows ONE grid, chosen by the "Roster view"
  // dropdown. Between 2026-08-03 and 2026-08-25 today's schedule was
  // instead overlaid on the optimized grid as a second bar per agent,
  // behind a "Compare with current schedule" switch -- that was two
  // controls over one question (which roster am I looking at) and the
  // switch could not answer it alone, since an overlay is not a view.
  const [optimizedRoster, setOptimizedRoster] = React.useState<PlanRosterOptimizedResponse | null>(null);
  const [optimizedLoading, setOptimizedLoading] = React.useState(true);
  const [optimizedError, setOptimizedError] = React.useState<string | null>(null);
  const [rosterDownloading, setRosterDownloading] = React.useState(false);
  // Which roster the section is showing. 8 hours is the default because it
  // is the shift the customer agreed to; 10 and 12 exist because the 8-hour
  // roster schedules LESS total time than the team's current schedules
  // provide, so seeing what the same people cover on a longer day is the
  // headcount conversation this page is meant to start.
  const [rosterView, setRosterView] = React.useState<RosterViewId>('h8');
  // Set only while a SOLVED view is being fetched. Switching to today's
  // schedule reads the as-given agents already on the payload, so it is
  // instant and must not draw a loader.
  const [rosterUpdating, setRosterUpdating] = React.useState(false);

  const handleDownloadRosterXlsx = async () => {
    setRosterDownloading(true);
    setOptimizedError(null);
    try {
      // The view on screen, so the workbook can never disagree with the
      // grid it was downloaded from.
      await downloadPlanRosterOptimizedXlsx(rosterViewById(rosterView).hours ?? undefined);
    } catch (e: unknown) {
      setOptimizedError(e instanceof Error ? e.message : 'Failed to download roster workbook');
    } finally {
      setRosterDownloading(false);
    }
  };

  // Only the roster depends on the shift length, so switching views
  // refetches this one section rather than the whole page. Each view is a
  // separate solve behind its own cache entry, so the first switch to a
  // cold view is slow and the ones after are not.
  const loadRoster = React.useCallback((hours: number, opts?: FetchOpts) => {
    setOptimizedLoading(true);
    setOptimizedError(null);
    return fetchPlanRosterOptimized({ ...opts, hours })
      .then(setOptimizedRoster)
      .catch((e: unknown) => setOptimizedError(e instanceof Error ? e.message : 'Failed to load the optimized roster'))
      .finally(() => setOptimizedLoading(false));
  }, []);

  // Read through a ref so a view change doesn't invalidate loadAll and
  // refetch the Plan and schedule sections along with it.
  const rosterViewRef = React.useRef(rosterView);
  rosterViewRef.current = rosterView;

  const selectedRosterHours = rosterViewById(rosterView).hours;
  const rosterSubtitle =
    rosterView === 'current'
      ? 'The hours agents actually work today. Every other view is compared against this one.'
      : `Every agent works ${selectedRosterHours} hours, moved to the times of day we most need them.`;

  const handleRosterViewChange = (id: RosterViewId) => {
    if (id === rosterView) return;
    setRosterView(id);
    const hours = rosterViewById(id).hours;
    // Today's schedule is already on the payload -- no fetch, no loader.
    if (hours == null) return;
    // The grid keeps showing the previous roster underneath the shimmer
    // rather than collapsing to empty, so the section does not jump.
    setRosterUpdating(true);
    void loadRoster(hours).finally(() => setRosterUpdating(false));
  };

  const loadAll = React.useCallback((opts?: FetchOpts) => {
    setLoading(true);
    setError(null);
    const planPromise = fetchPlanCapacity(opts)
      .then(setPlan)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load Plan'))
      .finally(() => setLoading(false));

    setScheduleLoading(true);
    setScheduleError(null);
    const schedulePromise = fetchAgentSchedule(opts)
      .then(setSchedule)
      .catch((e: unknown) => setScheduleError(e instanceof Error ? e.message : 'Failed to load roster schedule'))
      .finally(() => setScheduleLoading(false));

    const optimizedPromise = loadRoster(rosterViewById(rosterViewRef.current).hours ?? 8, opts);

    return Promise.all([planPromise, schedulePromise, optimizedPromise]);
  }, [loadRoster]);

  React.useEffect(() => {
    if (!feature.enabled) return;
    loadAll();
  }, [feature.enabled, loadAll]);

  useRefreshRegistration(React.useCallback(() => loadAll({ refresh: true }), [loadAll]));
  useReportFreshness(plan?.computed_at);

  if (!feature.enabled) {
    return (
      <Box sx={{ maxWidth: 720 }}>
        <ContactOptimizationNav active="plan" />
        <Alert severity="info">
          Contact Optimization is not turned on for this workspace. Enable it under Settings &rarr; Features.
        </Alert>
      </Box>
    );
  }

  if (loading && !plan) {
    return (
      <Box>
        <ContactOptimizationNav active="plan" />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <ContactOptimizationNav active="plan" />
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!plan) return null;

  const { headline, gap_cost: gapCost } = plan;
  const dollarBasisNote = gapCost.dollar_basis.is_measured
    ? 'measured from our own recent CAS deals'
    : "a fallback estimate — we don't have enough closed CAS deals yet to measure this directly";
  const forecastAgentHoursPerWeek = plan.global_dials_per_agent_hour
    ? plan.recent_demand.total_weekly_calls / plan.global_dials_per_agent_hour
    : null;
  const { hours: currentWeeklyAgentHours, fromRoster: capacityFromRoster } = scheduledWeeklyAgentHours(
    optimizedRoster,
    schedule,
    plan.weekday_operating_days,
  );
  // Today's shifts, kept only to show what the standard shift changes.
  // Plain computation, not a hook -- this sits after the component's early
  // returns, where a hook would break the rules-of-hooks ordering.
  const todayWeeklyAgentHours = schedule
    ? schedule.agents.reduce(
        (sum, a) =>
          a.shift_start_hour == null || a.shift_end_hour == null
            ? sum
            : sum + (a.shift_end_hour - a.shift_start_hour),
        0,
      ) * plan.weekday_operating_days
    : null;

  // Dropped into every section's own scope chip tooltip below, so the
  // exact dates behind the window are always one hover away from wherever
  // they're actually used -- not stated once in a banner someone has to
  // scroll back up to. Single window as of 2026-08-12: Staffed, Forecast,
  // and everything else on this page now share the same recent lookback
  // (see `get_plan_capacity`'s own docstring for why the earlier
  // full-history/recent split was retired) -- one chip, not two to compare.
  const windowDetail = plan.scope?.window.min_call_dt && plan.scope.window.max_call_dt
    ? `${formatDateRange(plan.scope.window.min_call_dt, plan.scope.window.max_call_dt)}, weekdays only`
    : plan.scope
      ? `Last ${plan.scope.window.lookback_days} days, weekdays only`
      : 'Recent, weekdays only';
  const windowLabel = plan.scope ? `Last ${plan.scope.window.lookback_days} days` : 'Recent';

  return (
    <Box>
      <ContactOptimizationNav active="plan" />
      <SettingsPageHeader description="Do we have enough people, and are they working the right hours? Compares call demand against staffing, hour by hour." />

      <Paper
        elevation={0}
        sx={(t) => ({ p: 2, border: `1px solid ${t.tokens.border.subtle}`, borderRadius: `${t.ds.radius.md}px`, mb: 2, bgcolor: t.tokens.surface.raised })}
      >
        <ChartSectionHeader
          title="Staffing snapshot"
          subtitle="Do we have enough people, and are they working the right hours — based on real, recent call volume?"
          actions={
            <>
              {plan.scope && (
                <SectionScopeChip window={{ label: windowLabel, detail: `Powers every tile below — ${windowDetail}` }} />
              )}
              <Tooltip title={plan.concentration_guardrail.note}>
                <Chip
                  label={`Peak hour ${plan.concentration_guardrail.peak_hour_label ?? '\u2013'}: ${pct(plan.concentration_guardrail.peak_share_pct, 1)} of demand`}
                  size="small"
                  color={plan.concentration_guardrail.breaches_ceiling ? 'warning' : 'default'}
                  variant="outlined"
                />
              </Tooltip>
            </>
          }
        />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 1.5,
            mt: 1.5,
          }}
        >
        <MetricTile
          icon={ListAltRounded}
          label="Recent call volume"
          value={`${Math.round(plan.recent_demand.total_weekly_calls).toLocaleString()} calls/week`}
          delta={`last ${plan.recent_demand.lookback_days} days`}
          tooltip={`How many calls actually happened, averaged to a weekly figure over the last ${plan.recent_demand.lookback_days} days. No assumption about how many calls a lead "should" get — just real recent volume, which is what the Forecast line and the reshuffled roster are both sized against.`}
        />
        <MetricTile
          icon={CompareArrowsRounded}
          label="Forecast vs. scheduled capacity"
          value={
            forecastAgentHoursPerWeek != null
              ? `${(currentWeeklyAgentHours ?? 0).toFixed(0)} of ${forecastAgentHoursPerWeek.toFixed(0)} hours`
              : '\u2013'
          }
          delta={
            forecastAgentHoursPerWeek != null && currentWeeklyAgentHours != null
              ? currentWeeklyAgentHours >= forecastAgentHoursPerWeek
                ? 'covers the forecast'
                : `${(forecastAgentHoursPerWeek - currentWeeklyAgentHours).toFixed(0)} hours short, per week`
              : undefined
          }
          tooltip={`How many hours of calling the roster schedules per week (first number), against how many hours recent call volume actually needs (second). This is the headline number on this page: a gap here is a headcount problem, and no amount of reshuffling shifts can close it.${
            capacityFromRoster
              ? ` The first number is every agent on the standard 8-hour shift.${
                  todayWeeklyAgentHours
                    ? ` Their current shifts add up to ${todayWeeklyAgentHours.toFixed(0)} hours, but those are start-to-end spans that include each agent's break, so they overstate time actually on the phone.`
                    : ''
                }`
              : ' Taken from the shifts the team works today, while the roster loads.'
          }`}
        />
        <MetricTile
          icon={TrendingDownRounded}
          label="Biggest single gap"
          value={headline.biggest_gap ? `${headline.biggest_gap.shortfall_agents.toFixed(1)} agents` : '\u2013'}
          delta={headline.biggest_gap ? `at ${headline.biggest_gap.hour_label}` : undefined}
          tooltip="The single hour of the day with the biggest staffing shortfall, on the company (Eastern) clock — so it names an hour a shift can actually be scheduled into. See the chart below for the full shape."
        />
        <MetricTile
          icon={PaidRounded}
          label="Est. dollars at stake / week"
          value={fmtMoney(headline.estimated_dollars_at_stake_per_week)}
          tooltip={`An order-of-magnitude estimate, not a forecast — it chains four assumptions together. Understaffed hours mean missed calls, which mean missed connections, which mean missed sales: about ${gapCost.missed_connects_per_week.toFixed(0)} missed connections/week, roughly ${gapCost.connect_to_cas_pct?.toFixed(1) ?? '\u2013'}% of which would normally become a sale, worth about $${gapCost.dollar_basis.avg_commission_per_cas.toLocaleString()} in commission each (${dollarBasisNote}). Revenue at risk, not profit — it doesn't subtract what it costs to get or call the lead.`}
        />
        <MetricTile
          icon={PeopleAltRounded}
          label="Agents scheduled"
          value={schedule ? `${schedule.agents.length}` : '\u2013'}
          delta={schedule ? `${schedule.real_schedule_agent_count} on a real schedule \u00b7 ${schedule.inferred_schedule_agent_count} on a best guess` : undefined}
          tooltip="The agents this roster plans for — everyone on today's dialing team. Other people in this workspace also place calls (support and case-management roles); their work shows in the call volume above but they are not scheduled here."
        />
      </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={(t) => ({ p: 2, border: `1px solid ${t.tokens.border.subtle}`, borderRadius: `${t.ds.radius.md}px`, mb: 2 })}
      >
        <ChartSectionHeader
          title="Demand vs. staffed capacity, by hour"
          subtitle="Hour by hour on the company (Eastern) clock — the same clock the roster below is built on, so a gap here names an hour you can actually staff. Forecast is what recent call volume implies; Staffed is how many agents were really on the phone in that hour."
          actions={
            <>
              <Tooltip title="Download this hour-by-hour breakdown as CSV — volume, connect rate, staffed vs. required agents" arrow>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => exportPlanCsv(plan)}
                    startIcon={<DownloadRounded sx={{ fontSize: 16 }} />}
                  >
                    Download CSV
                  </Button>
                </span>
              </Tooltip>
              <ExplainerToggle
                title="What 'demand' and 'required agents' mean here"
                open={showDemandHelp}
                onClick={() => setShowDemandHelp((v) => !v)}
              />
            </>
          }
          secondaryActions={
            plan.scope ? (
              <Tooltip title={`The dashed line and the bars, all of them — ${windowDetail}`} arrow>
                <Box sx={{ width: '100%', cursor: 'help' }}>
                  <Typography variant="caption" color="text.secondary">
                    Showing {windowLabel.toLowerCase()}, company (Eastern) clock
                  </Typography>
                </Box>
              </Tooltip>
            ) : undefined
          }
        />
        <ExplainerNote title="What 'demand' and 'required agents' mean here" open={showDemandHelp}>
          {`Weekdays only, 8am-11pm Eastern, based on the last ${plan.scope?.window.lookback_days ?? 28} days of real call volume — not a full-year average, so it tracks the team and volume we actually have right now. Hours are on the company (Eastern) clock, the same one the roster uses.

**Every line and bar on this chart is a headcount (agents), never a rate or percentage — connect rate isn't plotted here at all.** If the dashed line looks like it should be a rate (e.g. compared to Timing's own dashed connect-rate lines), it isn't; check the legend name, which spells out "(agents)".

- **Demand** — how many calls come in during that hour, on an average day over the last ${plan.scope?.window.lookback_days ?? 28} days. *E.g. 9am might average 78 calls/day, 4pm only 30, so 9am is just a busier hour.*
- **Forecast (agents)** (orange dashed line) — how many agents that demand actually needs: real call volume from the last ${plan.recent_demand.lookback_days} days (about ${plan.recent_demand.total_weekly_calls.toLocaleString()} calls/week), placed into each state's own best hours to call first, using our measured rate of **${plan.global_dials_per_agent_hour?.toFixed(1) ?? '–'} calls per agent per hour**. ${plan.recent_demand.note}
- **Staffed** — how many agents actually worked that hour, over the same recent window.
- **Colored bars** — red = short-staffed that hour, green = overstaffed, compared to the Forecast line.
- **Background shading** — just shows which hours are busiest relative to each other on this chart; not a new number.

${plan.weekend_note}

This is demand only — we don't have real shift, time zone, or licensing data behind these numbers yet. See Roster below.`}
        </ExplainerNote>
        <CapacityChart plan={plan} />
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1, flexWrap: 'wrap' }}>
          {(['low', 'below_average', 'above_average', 'high'] as const).map((tier) => (
            <Stack key={tier} direction="row" spacing={0.5} alignItems="center">
              <Box
                sx={(t) => ({
                  width: 14,
                  height: 10,
                  borderRadius: '2px',
                  bgcolor: t.palette.primary.main,
                  opacity: { low: 0.04, below_average: 0.09, above_average: 0.16, high: 0.26 }[tier],
                  border: `1px solid ${t.tokens.border.subtle}`,
                })}
              />
              <Typography variant="caption" color="text.secondary">{DEMAND_TIER_LABELS[tier]}</Typography>
            </Stack>
          ))}
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={(t) => ({ p: 2, border: `1px solid ${t.tokens.border.subtle}`, borderRadius: `${t.ds.radius.md}px`, mb: 2 })}
      >
        <ChartSectionHeader
          title={
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Roster</Typography>
              {scheduleLoading && !schedule && <CircularProgress size={12} />}
            </Stack>
          }
          subtitle={rosterSubtitle}
          actions={
            <>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="caption" color="text.secondary">
                  Roster view
                </Typography>
                <Select
                  size="small"
                  value={rosterView}
                  onChange={(e) => handleRosterViewChange(e.target.value as RosterViewId)}
                  sx={{ minWidth: 190, '& .MuiSelect-select': { py: 0.5, fontSize: 13 } }}
                >
                  <MenuItem value="current" sx={ROSTER_ITEM_SX}>
                    {rosterViewById('current').label}
                  </MenuItem>
                  <Divider sx={{ my: 0.5 }} />
                  {ROSTER_VIEWS.filter((v) => v.id !== 'current').map((v) => (
                    <MenuItem key={v.id} value={v.id} disabled={v.disabled} sx={ROSTER_ITEM_SX}>
                      {v.label}
                      {v.note ? (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>
                          {v.note}
                        </Typography>
                      ) : null}
                    </MenuItem>
                  ))}
                </Select>
                {rosterUpdating && <CircularProgress size={14} />}
              </Stack>
              {/* The workbook is the optimized roster -- the thing the customer
                  acts on. Today's schedule is the baseline it is measured
                  against and is not a deliverable, so the button is hidden
                  rather than shown downloading something else. */}
              {rosterView !== 'current' && optimizedRoster && optimizedRoster.optimized.assignments.length ? (
                <Tooltip title="Download the color-coded roster, plus a Week-at-a-glance and Weekday-detail sheet, as one Excel workbook" arrow>
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleDownloadRosterXlsx}
                      disabled={rosterDownloading}
                      startIcon={rosterDownloading ? <CircularProgress size={14} /> : <DownloadRounded sx={{ fontSize: 16 }} />}
                    >
                      Download roster (.xlsx)
                    </Button>
                  </span>
                </Tooltip>
              ) : null}
              <ExplainerToggle title="Where this schedule comes from" open={showRosterHelp} onClick={() => setShowRosterHelp((v) => !v)} />
            </>
          }
        />

        <ExplainerNote title="Where this schedule comes from" open={showRosterHelp}>
          {`**Today's schedule (what agents actually work):**

${
            schedule && schedule.real_schedule_agent_count > 0
              ? `${schedule.real_schedule_agent_count} of ${schedule.agents.length} agents (green dot) have a **real, written schedule**${schedule.real_schedule_source_files.length ? ` from ${schedule.real_schedule_source_files.join(', ')}` : ''}. It's a one-time import, not a live sync, so if it changes, someone has to re-upload it.${
                  schedule.inferred_schedule_agent_count > 0
                    ? ` The other ${schedule.inferred_schedule_agent_count} (grey dot) aren't in that file, so we guess their shift from their last ${schedule.lookback_days} days of calls: an 8-hour block plus a 1-hour break, placed wherever their calls are actually concentrated.`
                    : ''
                }`
              : `We don't have any real written schedules yet, so every agent's "shift" here is a guess based on their own last ${schedule ? schedule.lookback_days : 60} days of calls: an 8-hour block plus a 1-hour break, placed wherever their calls are actually concentrated. Agents with too little history to guess reliably are **grayed out**.`
          }

**The optimized schedule:** same agents, each on a **${selectedRosterHours ?? 8}-hour** shift regardless of the hours they work today, placed wherever coverage is thinnest relative to recent demand — ${rosterShapeSentence(selectedRosterHours ?? 8)}, so a break always falls before anyone reaches 5 continuous hours. Where ${selectedRosterHours ?? 8} hours is fewer than an agent works today, the optimized schedule carries less total time than the current one, and expected connects fall with it — placement cannot make up a difference in total hours. Use the **Roster view** dropdown above to rebuild it on a longer day; that is more staffing, not smarter placement, so treat the difference as a headcount question. It matches coverage **proportionally** to recent demand in each hour rather than chasing the best-converting hours outright: an hour that measures a touch better never gets fully staffed while a weaker one is left empty, since leaving any reachable hour at zero is treated as far worse than being evenly thin everywhere. The forecast curve itself already favors better-converting hours with a bigger share, so this still steers more people there — just never all of them.

It can move someone far from their usual start if that spreads coverage better; the one rule it won't break is a hard window of **8am–11pm Eastern** (the reference clock this whole grid is shown on) — the same cutoff for every agent no matter their own timezone, so a Pacific agent can start as early as 5am their own time as long as it's 8am here. No one is ever scheduled outside that window on this clock, even if that's their real, written shift today. Which named agent lands on which resulting shift is then decided separately, to move as few people as possible from their current start — coverage is identical either way, so nothing is traded away to keep the roster stable between refreshes.

**"On shift / needed"** at the top of the grid compares who's on shift against how many the forecast actually needs that hour. If that gap looks big, it's usually a headcount problem (not enough people scheduled), not something a reshuffle alone can fix.

We don't yet have state-licensing data, so a big move (the badge on each agent) could in theory land someone on hours for a state they're not licensed to call — worth a quick sanity check before acting on any large jump.${
            optimizedRoster?.optimized.notes.length
              ? `\n\n${optimizedRoster.optimized.notes.map((note) => `- ${note}`).join('\n')}`
              : ''
          }`}
        </ExplainerNote>

        {scheduleError && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {scheduleError} — the "Today's schedule" roster view may be incomplete.
          </Alert>
        )}

        {optimizedLoading && !optimizedRoster && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        )}
        {optimizedError && <Alert severity="error">{optimizedError}</Alert>}
        {optimizedRoster && (optimizedRoster.optimized.assignments.length ? (
          <OptimizedRosterView
            optimized={optimizedRoster.optimized}
            asGivenAgents={optimizedRoster.as_given.agents}
            referenceTimezone={optimizedRoster.reference_timezone}
            hours={optimizedRoster.roster_hours}
            statePriority={optimizedRoster.state_priority_by_reference_hour}
            view={rosterView}
            updating={rosterUpdating}
            solvedShiftHours={optimizedRoster.shift_work_hours ?? null}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            No agents with a usable current shift to reshuffle.
          </Typography>
        ))}
      </Paper>

    </Box>
  );
}
